/**
 * Reusable Progress Tracking System
 * Provides database-based progress tracking for long-running operations
 */

import { getSessionUUID } from '../components/lib/AuthToken';

/**
 * Start a background process with progress tracking
 * @param {string} endpoint - The API endpoint to call to start the process
 * @param {object} payload - Optional payload to send with the request
 * @returns {Promise<string>} - Returns the process UUID for tracking
 */
export const startProgressTracking = async (endpoint, payload = {}) => {
    try {
        const sessionToken = await getSessionUUID();
        
        if (!sessionToken) {
            throw new Error('No authentication token found');
        }

        const startResponse = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(payload)
        });

        if (!startResponse.ok) {
            throw new Error(`Failed to start process: ${startResponse.status}`);
        }

        const startResult = await startResponse.json();
        return startResult.uuid;
        
    } catch (error) {
        throw error;
    }
};

/**
 * Track progress of a running process
 * @param {string} processUuid - The UUID of the process to track
 * @param {function} onProgress - Callback function for progress updates
 * @param {object} options - Configuration options
 * @returns {Promise<object>} - Returns the final result when complete
 */
export const trackProgress = async (processUuid, onProgress = () => {}, options = {}) => {
    const {
        pollInterval = 5000,        // Poll every 5 seconds
        maxRetries = 3,             // Retry failed requests 3 times
        timeout = 1800000,          // 30 minute timeout
        progressEndpoint = '/enhanced_warehouse/bulk-regenerate-qr-progress'  // Default endpoint
    } = options;

    try {
        const sessionToken = await getSessionUUID();
        
        if (!sessionToken) {
            throw new Error('No authentication token found');
        }

        return new Promise((resolve, reject) => {
            let retryCount = 0;
            
            const pollInterval_id = setInterval(async () => {
                try {
                    const progressResponse = await fetch(`${progressEndpoint}/${processUuid}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    });

                    if (!progressResponse.ok) {
                        retryCount++;
                        if (retryCount >= maxRetries) {
                            clearInterval(pollInterval_id);
                            reject(new Error(`Failed to get progress after ${maxRetries} retries: ${progressResponse.status}`));
                        }
                        return; // Don't clear interval, just skip this poll and retry next time
                    }
                    
                    // Reset retry count on successful response
                    retryCount = 0;

                    const progressData = await progressResponse.json();
                    
                    // Call progress callback
                    onProgress({
                        processed: progressData.processed || 0,
                        total: progressData.total || 0,
                        message: progressData.message || 'Processing...',
                        success_count: progressData.success_count || 0,
                        error_count: progressData.error_count || 0,
                        errors: progressData.errors || []
                    });

                    // Check if completed
                    if (progressData.completed) {
                        clearInterval(pollInterval_id);
                        resolve({
                            success: true,
                            message: progressData.message,
                            processed: progressData.processed,
                            total: progressData.total,
                            success_count: progressData.success_count,
                            error_count: progressData.error_count,
                            errors: progressData.errors
                        });
                    }
                } catch (pollError) {
                    retryCount++;
                    if (retryCount >= maxRetries) {
                        clearInterval(pollInterval_id);
                        reject(pollError);
                    }
                    // Don't clear interval on network errors, just retry
                }
            }, pollInterval);

            // Add timeout
            setTimeout(() => {
                clearInterval(pollInterval_id);
                reject(new Error(`Process timed out after ${timeout / 1000 / 60} minutes`));
            }, timeout);
        });
        
    } catch (error) {
        throw error;
    }
};

/**
 * Complete progress tracking workflow - start process and track to completion
 * @param {string} startEndpoint - API endpoint to start the process
 * @param {object} payload - Payload to send when starting
 * @param {function} onProgress - Progress callback function
 * @param {object} options - Configuration options
 * @returns {Promise<object>} - Returns the final result
 */
export const executeWithProgressTracking = async (startEndpoint, payload, onProgress, options = {}) => {
    try {
        // Step 1: Start the process
        const processUuid = await startProgressTracking(startEndpoint, payload);
        
        // Step 2: Track progress to completion
        const result = await trackProgress(processUuid, onProgress, options);
        
        return result;
    } catch (error) {
        throw error;
    }
};